import * as React from "react";
import { useDialog } from "@shared-ui/dialog-context";
import { ItemKeyHelper } from "components/utility/ItemKeyHelper";
import LinksGridItem from "components/flexComponents/Links/GridView/LinksGridItem";
import { LinkDialog } from "components/flexComponents/Links/GridView/LinkDialog";
import { UitkLayoutGrid } from "uitk-react-layout-grid";
import { UitkCard, UitkCardContentSection } from "uitk-react-cards";
import { ExtendedContextStore } from "typings/flexFramework/FlexDefinitions";
import { Viewport, ViewSmall, ViewMedium, ViewLarge } from "@shared-ui/viewport-context";
import { UitkSpacing } from "uitk-react-spacing";
import { BlossomComponent } from "src/components/flexFramework/domain/BlossomComponent";
import { TemplateComponent } from "experience-template-renderer-react";
import { withStores } from "stores";

interface LinksGridWithDialogProps {
  groups?: BlossomComponent[];
  view: string;
  id: string;
  context: ExtendedContextStore;
  templateComponents?: TemplateComponent[];
}

const LinksGridWithDialog = withStores("context")((props: LinksGridWithDialogProps) => {
  const { groups, view, id, context, templateComponents } = props;

  if (groups) {
    return (
      <Viewport>
        <ViewSmall>
          <LinksGridWithDialogDom groups={groups} view={view} id={id} context={context} columns={1} />
        </ViewSmall>
        <ViewMedium>
          <LinksGridWithDialogDom
            groups={groups}
            view={view}
            id={id}
            context={context}
            columns={groups.length === 1 ? 1 : 2}
          />
        </ViewMedium>
        <ViewLarge>
          <LinksGridWithDialogDom
            groups={groups}
            view={view}
            id={id}
            context={context}
            columns={groups.length === 1 ? 1 : 3}
          />
        </ViewLarge>
      </Viewport>
    );
  }

  if (templateComponents) {
    return (
      <Viewport>
        <ViewSmall>
          <LinksGridWithDialogDom
            templateComponents={templateComponents}
            view={view}
            id={id}
            context={context}
            columns={1}
          />
        </ViewSmall>
        <ViewMedium>
          <LinksGridWithDialogDom
            templateComponents={templateComponents}
            view={view}
            id={id}
            context={context}
            columns={templateComponents.length === 1 ? 1 : 2}
          />
        </ViewMedium>
        <ViewLarge>
          <LinksGridWithDialogDom
            templateComponents={templateComponents}
            view={view}
            id={id}
            context={context}
            columns={templateComponents.length === 1 ? 1 : 3}
          />
        </ViewLarge>
      </Viewport>
    );
  }

  return null;
});

interface LinksGridWithDialogDomProps {
  groups?: BlossomComponent[];
  view: string;
  id: string;
  context: ExtendedContextStore;
  columns: any;
  templateComponents?: TemplateComponent[];
}

const LinksGridWithDialogDom = ({
  groups,
  view,
  id,
  context,
  columns,
  templateComponents,
}: LinksGridWithDialogDomProps) => {
  const focusLinkGridRef = React.useRef<any>();
  const [, dialogActions, LinkDialogComponent] = useDialog(`linksDialog-${id}`);
  const keyHelper = new ItemKeyHelper("LinksGroup");
  const linksCardView =
    view === "standard-link" && (groups?.length === 1 || templateComponents?.length === 1) ? "unset" : "two";
  const onDismiss = () => {
    dialogActions.closeDialog();
    focusLinkGridRef.current?.focus();
  };

  return (
    <div className="LinksGridWithDialog">
      <UitkCard>
        <UitkSpacing padding={{ inline: linksCardView }}>
          <UitkCardContentSection padded={false}>
            <UitkLayoutGrid space="one" columns={columns}>
              {groups?.map((group) => (
                <LinksGridItem
                  key={keyHelper.next()}
                  view={view}
                  group={group}
                  id={id}
                  context={context}
                  focusLinkGridRef={focusLinkGridRef}
                />
              ))}
              {templateComponents?.map((templateComponent) => (
                <LinksGridItem
                  key={keyHelper.next()}
                  view={view}
                  templateComponent={templateComponent}
                  id={id}
                  context={context}
                  focusLinkGridRef={focusLinkGridRef}
                />
              ))}
              <LinkDialogComponent>
                {groups ? (
                  <LinkDialog key={keyHelper.next()} groups={groups} id={id} context={context} onDismiss={onDismiss} />
                ) : templateComponents ? (
                  <LinkDialog
                    key={keyHelper.next()}
                    templateComponents={templateComponents}
                    id={id}
                    context={context}
                    onDismiss={onDismiss}
                  />
                ) : (
                  <LinkDialog key={keyHelper.next()} id={id} context={context} onDismiss={onDismiss} />
                )}
              </LinkDialogComponent>
            </UitkLayoutGrid>
          </UitkCardContentSection>
        </UitkSpacing>
      </UitkCard>
    </div>
  );
};

export default LinksGridWithDialog;
